import axios from "axios";
import { ErrorMessage, Field } from "formik";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import _ from "lodash";
import { AppContext } from "state";
import { renderBaseUrlForApi } from "utils";
import classNames from "classnames";
// Make sure to import your Calendar icon here. For example:
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const CalendarPopover = ({ value, onChange, disable }: { value: string; onChange: (v: string) => void; disable?: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"days" | "months" | "years">("days");
  const [viewDate, setViewDate] = useState(() => {
    if (value && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [d, m, y] = value.split("/").map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date();
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const yearGridRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({});

  const selectedDate = (() => {
    if (value && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [d, m, y] = value.split("/").map(Number);
      return new Date(y, m - 1, d);
    }
    return null;
  })();

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ position: "fixed", top: r.bottom + 6, right: window.innerWidth - r.right, zIndex: 9999 });
    }
    if (isOpen) setMode("days");
  }, [isOpen]);

  useEffect(() => {
    if (mode === "years" && yearGridRef.current) {
      const activeBtn = yearGridRef.current.querySelector("[data-active='true']") as HTMLElement;
      if (activeBtn) activeBtn.scrollIntoView({ block: "center", behavior: "auto" });
    }
  }, [mode]);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) && !panelRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const handleSelectDay = (day: number) => {
    const dd = String(day).padStart(2, "0");
    const mm = String(month + 1).padStart(2, "0");
    onChange(`${dd}/${mm}/${year}`);
    setIsOpen(false);
  };

  const handleSelectMonth = (m: number) => {
    setViewDate(new Date(year, m, 1));
    setMode("days");
  };

  const handleSelectYear = (y: number) => {
    setViewDate(new Date(y, month, 1));
    setMode("months");
  };

  const btnBase: React.CSSProperties = { border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", transition: "all 0.12s ease" };

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => !disable && setIsOpen((p) => !p)}
        className={`absolute right-[0.3vw] top-1/2 -translate-y-1/2 w-[1.4vw] h-[1.4vw] flex items-center justify-center rounded-[4px] transition-all duration-150 ${
          disable ? "text-[#D1D5DB] cursor-not-allowed" : "text-[#6B7280] hover:text-[#374AF2] hover:bg-[#F3F4F6] cursor-pointer"
        }`}
      >
        <Calendar className="w-[1.1vw] h-[1.1vw]" strokeWidth={1.5} />
      </div>
      {isOpen && createPortal(
        <div ref={panelRef} style={pos}>
          <div style={{
            width: 280, background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)", fontFamily: "Inter, sans-serif", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderBottom: "1px solid #F3F4F6" }}>
              <button type="button" onClick={() => {
                if (mode === "days") setViewDate(new Date(year, month - 1, 1));
                else if (mode === "months") setViewDate(new Date(year - 1, month, 1));
                else setViewDate(new Date(year - 12, month, 1));
              }}
                style={{ ...btnBase, width: 28, height: 28, borderRadius: 6, background: "transparent", color: "#374151" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <ChevronLeft size={16} />
              </button>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" onClick={() => setMode(mode === "months" ? "days" : "months")}
                  style={{ ...btnBase, background: mode === "months" ? "#EEF2FF" : "transparent", color: mode === "months" ? "#374AF2" : "#111827", fontSize: 13, fontWeight: 600, borderRadius: 6, padding: "4px 8px" }}
                  onMouseEnter={(e) => { if (mode !== "months") e.currentTarget.style.background = "#F3F4F6"; }}
                  onMouseLeave={(e) => { if (mode !== "months") e.currentTarget.style.background = "transparent"; }}
                >
                  {MONTH_NAMES[month]}
                </button>
                <button type="button" onClick={() => setMode(mode === "years" ? "days" : "years")}
                  style={{ ...btnBase, background: mode === "years" ? "#EEF2FF" : "transparent", color: mode === "years" ? "#374AF2" : "#111827", fontSize: 13, fontWeight: 600, borderRadius: 6, padding: "4px 8px" }}
                  onMouseEnter={(e) => { if (mode !== "years") e.currentTarget.style.background = "#F3F4F6"; }}
                  onMouseLeave={(e) => { if (mode !== "years") e.currentTarget.style.background = "transparent"; }}
                >
                  {year}
                </button>
              </div>
              <button type="button" onClick={() => {
                if (mode === "days") setViewDate(new Date(year, month + 1, 1));
                else if (mode === "months") setViewDate(new Date(year + 1, month, 1));
                else setViewDate(new Date(year + 12, month, 1));
              }}
                style={{ ...btnBase, width: 28, height: 28, borderRadius: 6, background: "transparent", color: "#374151" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {mode === "days" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "8px 10px 4px" }}>
                  {DAY_LABELS.map((d) => (
                    <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9CA3AF", padding: "2px 0" }}>{d}</div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "2px 10px 10px", gap: "2px" }}>
                  {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
                    const thisDate = new Date(year, month, d);
                    const isToday = thisDate.getTime() === today.getTime();
                    const isSelected = selectedDate && thisDate.getTime() === selectedDate.getTime();
                    return (
                      <button key={d} type="button" onClick={() => handleSelectDay(d)}
                        style={{
                          ...btnBase, width: 32, height: 32, borderRadius: 8,
                          fontSize: 13, fontWeight: isSelected ? 600 : 400,
                          background: isSelected ? "#374AF2" : "transparent",
                          color: isSelected ? "#fff" : isToday ? "#374AF2" : "#374151",
                          outline: isToday && !isSelected ? "1.5px solid #374AF2" : "none",
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F3F4F6"; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {mode === "months" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, padding: 10 }}>
                {MONTH_NAMES.map((m, i) => {
                  const isCurrent = i === currentMonth && year === currentYear;
                  const isActive = i === month;
                  return (
                    <button key={m} type="button" onClick={() => handleSelectMonth(i)}
                      style={{
                        ...btnBase, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: isActive ? 600 : 400,
                        background: isActive ? "#374AF2" : "transparent",
                        color: isActive ? "#fff" : isCurrent ? "#374AF2" : "#374151",
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#F3F4F6"; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      {m.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            )}

            {mode === "years" && (
              <div ref={yearGridRef} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, padding: 10, maxHeight: 240, overflowY: "auto" }}>
                {Array.from({ length: 101 }, (_, i) => 1950 + i).map((y) => {
                  const isCurrent = y === currentYear;
                  const isActive = y === year;
                  return (
                    <button key={y} type="button" onClick={() => handleSelectYear(y)} data-active={isActive ? "true" : undefined}
                      style={{
                        ...btnBase, padding: "8px 0", borderRadius: 8, fontSize: 13, fontWeight: isActive ? 600 : 400,
                        background: isActive ? "#374AF2" : "transparent",
                        color: isActive ? "#fff" : isCurrent ? "#374AF2" : "#374151",
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#F3F4F6"; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const Input: ({
  label,
  required,
  backgroundColor,
  name,
  type,
  placeHolder,
  customHeight,
  isMobile,
  labelClassNames,
  labelStyle,
  mapField,
  mapName,
  customFontSize,
  customStyle,
  disable,
  dominCheck,
  countryCodeName,
  mapButton,
  helperText,
  onClick,
}: {
  label?: string;
  required?: boolean;
  backgroundColor?: string;
  name: string;
  mapName?: string;
  type?: string;
  placeHolder?: string;
  customHeight?: string;
  labelClassNames?: string;
  labelStyle?: React.CSSProperties;
  mapField?: boolean;
  customFontSize?: string;
  customStyle?: any;
  disable?: boolean;
  isMobile?: boolean;
  dominCheck?: boolean;
  countryCodeName?: string;
  wantButton?: string;
  mapButton?: React.ReactNode;
  helperText?: string;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
}) => React.JSX.Element = ({
  label,
  required,
  backgroundColor,
  name,
  type,
  placeHolder,
  mapField,
  mapName,
  labelClassNames,
  labelStyle,
  customHeight,
  customFontSize,
  customStyle,
  disable,
  isMobile,
  dominCheck,
  countryCodeName,
  wantButton,
  mapButton,
  helperText,
  onClick,
}) => {
    const context = useContext(AppContext);
    const { globalData = { countries: [] } } = context || {};

    const [domainIsExist, setDomainIsExist] = useState("");

    const updateLocationFromAddress = async (enteredAddress, name, form) => {
      try {
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            enteredAddress
          )}&key=${process.env.REACT_APP_GOOGLE_API_KEY!}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response?.data?.results?.length > 0) {
          const location = response?.data?.results?.[0]?.geometry?.location;
          const formattedAddress =
            response?.data?.results?.[0]?.formatted_address;
          let place_id = response?.data?.results?.[0]?.place_id;
          let city = "";
          let postalCode = "";
          let country = "";
          let state = "";
          response?.data?.results?.[0]?.address_components.forEach(
            (component) => {
              if (component.types.includes("postal_code")) {
                postalCode = component.long_name;
              }
              if (component.types.includes("country")) {
                country = component.long_name;
              }
              if (component.types.includes("administrative_area_level_3")) {
                city = component.long_name;
              }
              if (component.types.includes("administrative_area_level_1")) {
                state = component.long_name;
              }
            }
          );
          form.setFieldValue(mapName, {
            lat: location.lat,
            lng: location.lng,
            address: formattedAddress,
            city,
            state,
            country,
            postalCode,
            place_id,
          });
        }
      } catch (error) {
        // Handle error if needed
      }
    };

    const debouncedUpdateLocation = useCallback(
      _.debounce((item, mapName, form) => {
        updateLocationFromAddress(item, mapName, form);
      }, 500),
      []
    );

    const handleChange = (event, form) => {
      const item = event.target.value;
      if (mapField && !!item) {
        debouncedUpdateLocation(item, mapName, form);
      }
      form.setFieldValue(name, item);
    };
    // 1. The actual API call logic
    const checkDomainAvailability = async (domainName, form, fieldName) => {
      if (!domainName) {
        setDomainIsExist("");
        form.setFieldError(fieldName, "");
        return;
      }
      try {
        const response = await axios.get(
          `${renderBaseUrlForApi()}/master/institute/checkDomainDuplicate?domain_name=${domainName}`
        );
        if (response?.data?.message !== "Domain name is available") {
          setDomainIsExist("YES");
          form.setFieldError(fieldName, "Domain name is not available");
        } else {
          setDomainIsExist("NO");
          form.setFieldError(fieldName, "");
        }
      } catch (err) {
        setDomainIsExist("");
      }
    };

    // 2. The 500ms delay wrapper
    const debouncedDomainCheck = useCallback(
      _.debounce((domainName, form, fieldName) => {
        checkDomainAvailability(domainName, form, fieldName);
      }, 500),
      []
    );
    return (
      <div className={`flex flex-col gap-[0.198vw] w-full relative ${helperText ? "pb-[3.8vh]" : "pb-[2.2vh]"}`}>
        {!!label && (
          <label
           className={`font-inter font-[500] text-[1.75vh] text-[#374151] mb-[0.4vh] pl-[0.432vw] tracking-[0.01em] ${labelClassNames || ""}`}  
          >
            {label} {!!required && <span style={{ color: '#E24B4A' }}>*</span>}
          </label>
        )}
        <Field name={name}>
          {({ field, form, meta }) => {
            const displayValue =
              typeof field.value === "object" && field.value !== null
                ? field.value.address
                : field.value || "";

            return (
              <div
                className={`custom-focus-ring flex items-stretch w-full border-[0.09vw] rounded-[5.4px] overflow-hidden pl-[0.396vw] bg-[#fff] min-h-[4.5vh] ${(meta.touched && meta.error) || domainIsExist === "YES"
                  ? "border-[#ff0000] animate-shake"
                  : "border-[#9d9d9d] "
                  }`}
              >
                <style>{`
                @keyframes shake {
                  0%, 100% { transform: translateX(0); }
                  20% { transform: translateX(-0.243vw); }
                  40% { transform: translateX(0.243vw); }
                  60% { transform: translateX(-0.243vw); }
                  80% { transform: translateX(0.243vw); }
                }
                .animate-shake {
                  animation: shake 0.4s ease-in-out;
                }
                .custom-focus-ring:focus-within {
                  outline: 2px solid #9B97F0;
                  outline-offset: 1px;
                }
              `}</style>

                {/* {isMobile && (
                  <Field name={countryCodeName || "COUNTRY_CODE"}>
                    {({ field, form, meta }) => (
                      <div className="relative flex-shrink-0 w-[7vh] flex border-r border-[#9d9d9d] mr-[3.6px]">
                        <select
                          {...field}
                          value={field.value || ""}
                          /* ADDED: bg-[position:right_0px_center] to push the arrow to the edge */
                          /* ADDED: pr-4 (optional) to ensure the word "Code" doesn't overlap the arrow */
                //           className={`w-full h-full min-h-[4.5vh] text-[1.765vh] font-inter font-[300] appearance-none bg-transparent border-none focus:outline-none focus:ring-0 cursor-pointer bg-[position:right_0px_center] pr-4 ${meta.touched && meta.error ? "text-red-600" : "text-black"
                //             }`}
                //         >
                //           <option value="" disabled hidden>Code</option>
                //           {globalData?.countries?.map((country) => (
                //             <option
                //               key={country.COUNTRY_CODE}
                //               value={country.COUNTRY_CODE}
                //             >
                //               {country.COUNTRY_CODE}
                //             </option>
                //           ))}
                //         </select>
                //       </div>
                //     )}
                //   </Field>
                // )} */}

                <div className="flex justify-between items-stretch w-full relative">
                  {/* ---------------------------------------------------- */}
                  {/* Custom Date Input Logic                              */}
                  {/* ---------------------------------------------------- */}
                  {type === "date" ? (
                    <>
                      {/* 1. TEXT INPUT - Formats as DD/MM/YYYY while typing */}
                      <input
                        type="text"
                        disabled={disable}
                        value={displayValue}
                        onChange={(e) => {
                          // Strip out anything that isn't a number
                          const rawValue = e.target.value.replace(/\D/g, "");

                          // Auto-insert slashes
                          let formattedValue = rawValue;
                          if (rawValue.length > 2 && rawValue.length <= 4) {
                            formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
                          } else if (rawValue.length > 4) {
                            formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2, 4)}/${rawValue.slice(4, 8)}`;
                          }

                          // Save the formatted DD/MM/YYYY string to form state
                          form.setFieldValue(name, formattedValue);
                        }}
                        className="w-full px-[0.396vw] text-[1.765vh] font-inter font-[400] focus:outline-none focus:ring-0 focus:border-transparent placeholder:text-[#9CA3AF] placeholder:text-[1.765vh] placeholder:font-inter placeholder:font-[400]"
                        placeholder={placeHolder || "DD/MM/YYYY"}
                        style={{
                          backgroundColor: backgroundColor || "transparent",
                          color: "#000",
                          fontFamily: "inter",
                          fontWeight: "400",
                          fontSize: "1.765vh",
                          height: "100%",
                          ...(customStyle || {}),
                        }}
                      />

                      <CalendarPopover
                        value={displayValue}
                        onChange={(dateStr: string) => form.setFieldValue(name, dateStr)}
                        disable={disable}
                      />
                    </>
                  ) : (
                    /* ---------------------------------------------------- */
                    /* Standard Input Logic                                 */
                    /* ---------------------------------------------------- */
                    <input
                      disabled={disable}
                      className={classNames(
                        "w-full px-[0.396vw] text-[1.765vh] font-inter font-[300] focus:outline-none focus:ring-0 focus:border-transparent",
                        "placeholder:text-[#9CA3AF] placeholder:text-[1.765vh] placeholder:font-inter placeholder:font-[400]",
                        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"

                      )}
                      placeholder={placeHolder || ""}
                      onClick={onClick}
                      value={displayValue}
                      style={{
                        backgroundColor: backgroundColor || "white",
                        textOverflow: "ellipsis",
                        fontSize: "1.962vh",
                        fontWeight: "400",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        color: "#000",
                        height: "100%",
                        ...(customStyle || {}),
                      }}
                      type={type === "number" ? "text" : (type || "text")}
                      inputMode={type === "number" ? "numeric" : undefined}
                      required={required}
                      onChange={(item) => {
                        let val = item.target.value;
                        if (type === "number") {
                          val = val.replace(/[^0-9]/g, "");
                        }
                        handleChange(
                          {
                            target: { value: isMobile ? val.slice(0, 10) : val },
                          },
                          form
                        );
                        if (dominCheck) {
                          debouncedDomainCheck(val, form, name);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      onKeyDown={(e) => {
                        if (
                          type === "number" &&
                          (e.key === "ArrowUp" || e.key === "ArrowDown")
                        ) {
                          e.preventDefault();
                        }
                      }}

                    />
                  )}

                  {Boolean(dominCheck && !!domainIsExist) ? (
                    <div
                      className="flex items-center justify-center w-[5.112vw]"
                      style={{ backgroundColor: backgroundColor || "white" }}
                    >
                      <img
                        src={
                          domainIsExist === "YES"
                            ? "/images/RedCross.svg"
                            : "/images/GreenTick.svg"
                        }
                        className="w-[0.999vw] h-[0.999vw]"
                        alt="Status"
                      />
                    </div>
                  ) : null}

                  {!!wantButton && (
                    <button
                      type="button"
                      disabled={true}
                      className="font-inter font-[400] px-[0.198vw] text-[1.765vh] w-[6.012vw] bg-[#eeeef0] border-l-[0.09vw] border-[#9d9d9d]"
                    >
                      {wantButton}
                    </button>
                  )}

                  {!!mapButton && (
                    <div
                      className="flex items-center justify-center w-[2.52vw] bg-[#eeeef0] text-gray-500 font-medium text-[0.693vw] border-l-[0.09vw] border-[#9d9d9d] h-full"
                      style={{ minWidth: "max-content" }}
                    >
                      {mapButton}
                    </div>
                  )}
                </div>}
              </div>
            );
          }}
        </Field>

        {/* Guaranteed Error Space */}
        <div className="absolute bottom-0 left-[0.4vw] right-0 flex flex-col justify-start overflow-hidden">
          {/* {isMobile && (
            <ErrorMessage
              name={countryCodeName || "COUNTRY_CODE"}
              component="div"
              className="text-[#E24B4A] text-[1.6vh] leading-none"
            />
          )} */}
          <ErrorMessage
            name={name}
            component="div"
            className="text-[#E24B4A] text-[1.6vh] leading-none"
          />
          {!!helperText && (
            <span className="text-[1.4vh] text-[#9CA3AF] font-inter font-[400] leading-none">{helperText}</span>
          )}
        </div>
      </div>
    );
  };

export default Input;